<template>
    <div :class="['hs-stats-card group w-full', { 'hs-stats-card--compact': compact }]" style="
      border-color: var(--hs-border);
      background: color-mix(in srgb, var(--hs-surface) 90%, transparent);
      box-shadow: var(--hs-shadow-card);
    ">
        <div class="hs-stats-card__glow"></div>
        <div class="hs-stats-card__inner">
            <div class="flex items-start justify-between gap-4">
                <div class="flex items-center space-x-4 flex-1 min-w-0">
                    <div :class="[
                        'flex items-center justify-center w-14 h-14 rounded-2xl transition-colors duration-200 hs-stats-card__icon',
                        cardIconClass,
                        'group-hover:scale-105',
                    ]">
                        <vue-feather :type="getIconType(iconType)" size="25" :class="iconColor" />
                    </div>

                    <div class="flex-1 min-w-0">
                        <div class="hs-stats-card__eyebrow">Live metric</div>
                        <div class="hs-stats-card__value cursor-pointer" :title="fullNumber">
                            {{ formattedNumber }}
                        </div>
                        <div class="hs-stats-card__label flex items-center gap-1 min-w-0">
                            <span class="truncate">{{ statsDescription }}</span>
                            <span v-if="descriptionTooltip" class="shrink-0 text-amber-500 dark:text-amber-300"
                                :title="descriptionTooltip">
                                <vue-feather type="alert-triangle" size="14" />
                            </span>
                        </div>
                        <div class="hs-stats-card__meta mt-3">
                            <div v-if="trend !== null" class="flex items-center mt-0.5">
                                <vue-feather :type="trend > 0 ? 'trending-up' : 'trending-down'" size="12"
                                    :class="trend > 0 ? 'text-green-500' : 'text-red-500'" />
                                <span :class="[
                                    'text-xs font-medium ml-1',
                                    trend > 0
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-red-600 dark:text-red-400',
                                ]">
                                    {{ Math.abs(trend) }}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex-shrink-0">
                    <div :class="[
                        'w-10 h-10 rounded-xl flex items-center justify-center opacity-40 transition-opacity duration-200 hs-stats-card__corner',
                        cardIconClass,
                        'group-hover:opacity-60',
                    ]">
                        <vue-feather :type="getIconType(iconType)" size="16" :class="iconColor" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "StatsCard",
    props: {
        iconColor: {
            type: String,
            default: "text-blue-600 dark:text-blue-400",
        },
        statsNumber: {
            type: String,
            default: "0",
        },
        statsDescription: {
            type: String,
            default: "Description",
        },
        descriptionTooltip: {
            type: String,
            default: "",
        },
        cardIconClass: {
            type: String,
            default: "bg-blue-100 dark:bg-blue-900/20",
        },
        iconType: {
            type: String,
            default: "users",
        },
        trend: {
            type: Number,
            default: null,
        },
        compact: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        formattedNumber() {
            if (!this.statsNumber && this.statsNumber !== 0) return "0";

            const numberStr = this.statsNumber.toString();
            const suffixes = ["K", "M", "B", "T"];
            const suffix = suffixes.find((s) => numberStr.endsWith(s));

            if (suffix) {
                const numericPart = numberStr.slice(0, -1);
                const num = parseFloat(numericPart.replace(/,/g, ""));

                if (!isNaN(num)) {
                    if (suffix === "M" && num >= 1000) {
                        return this.formatNumberWithSuffix(num / 1000, "B");
                    } else if (suffix === "K" && num >= 1000) {
                        return this.formatNumberWithSuffix(num / 1000, "M");
                    } else if (suffix === "B" && num >= 1000) {
                        return this.formatNumberWithSuffix(num / 1000, "T");
                    }

                    if (num >= 1000) {
                        return Math.floor(num).toLocaleString() + suffix;
                    }

                    return this.formatNumberWithSuffix(num, suffix);
                }
            }

            const num = parseFloat(numberStr);
            if (!isNaN(num)) {
                if (num >= 1e12) {
                    return this.formatNumberWithSuffix(num / 1e12, "T");
                } else if (num >= 1e9) {
                    return this.formatNumberWithSuffix(num / 1e9, "B");
                } else if (num >= 1e6) {
                    return this.formatNumberWithSuffix(num / 1e6, "M");
                } else if (num >= 1e3) {
                    return this.formatNumberWithSuffix(num / 1e3, "K");
                }
                return num.toLocaleString();
            }

            return numberStr;
        },
        fullNumber() {
            if (!this.statsNumber && this.statsNumber !== 0) return "0";

            const numberStr = this.statsNumber.toString();
            const suffixes = ["K", "M", "B", "T"];
            const suffix = suffixes.find((s) => numberStr.endsWith(s));

            if (suffix) {
                const numericPart = numberStr.slice(0, -1);
                const num = parseFloat(numericPart.replace(/,/g, ""));

                if (!isNaN(num)) {
                    let multiplier = 1;
                    switch (suffix) {
                        case "K":
                            multiplier = 1000;
                            break;
                        case "M":
                            multiplier = 1000000;
                            break;
                        case "B":
                            multiplier = 1000000000;
                            break;
                        case "T":
                            multiplier = 1000000000000;
                            break;
                    }

                    return (num * multiplier).toLocaleString();
                }
            }

            const num = parseFloat(numberStr);
            if (!isNaN(num)) {
                return num.toLocaleString();
            }

            return numberStr;
        },
    },
    methods: {
        formatNumberWithSuffix(value, suffix) {
            const formatted = parseFloat(value.toFixed(1));
            return formatted % 1 === 0
                ? formatted.toString() + suffix
                : formatted.toFixed(1) + suffix;
        },
        getIconType(iconType) {
            const iconMap = {
                "📊": "bar-chart-2",
                "💰": "dollar-sign",
                "💸": "trending-down",
                "🛒": "shopping-cart",
                "💵": "credit-card",
                users: "users",
                "shopping-bag": "shopping-bag",
                gift: "gift",
                "life-buoy": "life-buoy",
                "bar-chart-2": "bar-chart-2",
                "dollar-sign": "dollar-sign",
                "trending-down": "trending-down",
                "shopping-cart": "shopping-cart",
                "credit-card": "credit-card",
            };

            return iconMap[iconType] || iconType;
        },
    },
};
</script>

<style scoped>
.hs-stats-card {
    position: relative;
    overflow: hidden;
    border-radius: 24px;
    border-width: 1px;
    padding: 1.35rem;
    transition: transform 0.28s ease, box-shadow 0.28s ease;
}

.hs-stats-card:hover {
    transform: translateY(-2px);
}

.hs-stats-card__inner {
    position: relative;
    z-index: 1;
}

.hs-stats-card__glow {
    position: absolute;
    top: -2.5rem;
    right: -1rem;
    width: 8rem;
    height: 8rem;
    border-radius: 9999px;
    background: color-mix(in srgb, var(--hs-primary) 15%, transparent);
    filter: blur(40px);
    opacity: 0.8;
}

.hs-stats-card__icon {
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.hs-stats-card__eyebrow {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--hs-muted);
    margin-bottom: 0.35rem;
}

.hs-stats-card__value {
    font-size: clamp(1.65rem, 2vw, 2.25rem);
    line-height: 1.05;
    font-weight: 800;
    color: var(--hs-text);
    margin-bottom: 0.3rem;
}

.hs-stats-card__label {
    font-size: 0.92rem;
    font-weight: 600;
    color: var(--hs-muted);
}

.hs-stats-card__meta {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    flex-wrap: wrap;
}

.hs-stats-card__corner {
    backdrop-filter: blur(8px);
}

.hs-stats-card--compact {
    padding: 1rem;
    border-radius: 18px;
}

.hs-stats-card--compact :deep(.hs-stats-card__icon) {
    width: 2.65rem;
    height: 2.65rem;
    border-radius: 0.85rem;
}

.hs-stats-card--compact :deep(.hs-stats-card__value) {
    font-size: clamp(1.2rem, 1.5vw, 1.65rem);
}

.hs-stats-card--compact :deep(.hs-stats-card__label) {
    font-size: 0.82rem;
}

.hs-stats-card--compact :deep(.hs-stats-card__eyebrow) {
    font-size: 0.62rem;
    margin-bottom: 0.2rem;
}

.hs-stats-card--compact :deep(.hs-stats-card__meta) {
    margin-top: 0.45rem;
}
</style>
